import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import SearchResult from '@app/Views/Search/SearchResult';
import { CheckIcon, LoaderIcon, PlusIcon } from '@repo/icons';
import Contact from '@repo/utils/models/Contact';
import { useContactSearch } from '@repo/hooks';
import { SearchableOptionsModal } from '@repo/components/modals';
import { IDRecord, SVGProps } from '@repo/utils/types';
import { ListRowContent } from '@repo/components/lists';

interface ContactSearchModalProps {
  isOpen: boolean;
  closeModal: () => void;
  contactIsSelected?: (contact: Contact) => boolean;
  onOptionSelected: (contact: Contact) => void;
  className?: string;
  defaultContactSearchTerm?: string;
  resultCount?: number;
  // used to filter out contacts as needed
  resultFilter?: (idRecord: IDRecord) => boolean;
  // used to determine if contact has a checkmark or a plus icon next to it
  adjustResults?: (results: Array<IDRecord>) => Array<IDRecord | any>;
  // render options added by adjustResults that aren't Contacts
  renderNonContactOption?: (option: any) => ReactNode;
  header?: ReactNode;
  placeholder?: string;
  beforeOptions?: string;
  afterOptions?: ReactNode;
  noOptionsNodes?: () => { noOptionsMessage: string; noOptionsIcon: ReactNode };
  // this is run *before* the default onKeyDown handler. if it returns true, we return out of the default handler without running it.
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => boolean;
  useMainSearchSorting?: boolean;
}

export const ContactSearchModal = ({
  isOpen,
  closeModal,
  className,
  contactIsSelected,
  onOptionSelected,
  defaultContactSearchTerm = '',
  resultCount = 6,
  resultFilter = () => true,
  adjustResults = (x) => x,
  renderNonContactOption,
  header = null,
  placeholder = 'Search for a Person',
  beforeOptions,
  afterOptions,
  noOptionsNodes,
  onInputKeyDown,
  useMainSearchSorting = false,
}: ContactSearchModalProps) => {
  const [setContactSearchTerm, contactSearchState] = useContactSearch({
    resultCount,
    useMainSearchSorting,
    defaultTerm: defaultContactSearchTerm,
  });
  // This is the term that appears in the input field. This drives the contact search via an effect hook.
  const [searchTerm, setSearchTerm] = useState('');
  const searchResults = contactSearchState.results.items.filter(resultFilter).valueSeq().toArray();
  const asSectionedOptions = !!beforeOptions;

  const renderContact = useCallback(
    (contact) => {
      const isSelected = !!contactIsSelected?.(contact);
      const Icon = isSelected ? CheckIcon : PlusIcon;
      const iconProps: SVGProps = { width: 10, height: 10 };

      if (isSelected) {
        iconProps.customColor = '#2686be';
      }

      return (
        <SearchResult
          lookup
          before={
            <ListRowContent>
              <Icon {...iconProps} />
            </ListRowContent>
          }
          result={contact}
        />
      );
    },
    [contactIsSelected],
  );

  const renderSuggestion = useCallback(
    (item) => {
      const renderedContent = renderNonContactOption && renderNonContactOption(item);
      return renderedContent || renderContact(item);
    },
    [renderContact],
  );

  const defaultNoOptionsNodes = (): { noOptionsMessage: string; noOptionsIcon: ReactNode } => {
    let noOptionsMessage = 'No suggested matches.';
    let noOptionsIcon = undefined;
    if (searchTerm !== '') {
      noOptionsMessage = 'No results found.';
    }
    if (contactSearchState.results.isFetching) {
      noOptionsMessage = 'Searching your network...';
      noOptionsIcon = <LoaderIcon width={12} height={12} />;
    }
    return { noOptionsMessage, noOptionsIcon };
  };

  const { noOptionsMessage, noOptionsIcon } = (noOptionsNodes || defaultNoOptionsNodes)();

  const suggestions = useMemo<Contact[]>(
    () => (adjustResults ? adjustResults(searchResults) : searchResults),
    [searchResults],
  );

  return !isOpen ? null : (
    <SearchableOptionsModal
      overlayClassName={className}
      closeModal={closeModal}
      options={asSectionedOptions ? undefined : suggestions}
      // @ts-ignore
      optionSections={asSectionedOptions ? [{ suggestions, title: beforeOptions }] : undefined}
      renderOption={renderSuggestion}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      onOptionSelected={onOptionSelected}
      handleSearch={setContactSearchTerm}
      placeholder={placeholder}
      noOptionsMessage={noOptionsMessage}
      noOptionsIcon={noOptionsIcon}
      header={header}
      afterOptions={afterOptions}
      onInputKeyDown={onInputKeyDown}
    />
  );
};
